'use client';

import {
  ProductDetails,
  Pricing,
  isPricingEnabled,
  isCartEnabled,
} from '@peerless/data-access/cart-service';
import { ProductLink } from './ProductLink';
import { ProductAccessoryImage } from './ProductAccessoryImage';
import { Box, Button, Stack, Typography } from '@mui/material';

import { useFormatter, useTranslations } from 'next-intl';
import { getPricingServerAction } from './getPricingServerAction';

import { AddToCartButton } from '../../cart/components/AddToCartButton';

import { ProductTitle } from '@peerless/ui-shared';
import { useQuery } from '@tanstack/react-query';
import { useCurrentLocale } from '@peerless/ui-locale';

interface Props {
  direction: 'row' | 'column';
  product: ProductDetails;
  pricing?: Pricing;
}

export const ProductAccessoryTile: React.FC<Props> = ({ product, pricing, direction }: Props) => {
  const format = useFormatter();
  const t = useTranslations();
  const locale = useCurrentLocale();

  const query = useQuery({
    queryKey: ['Product', 'Pricing', product.sku],
    queryFn: () => getPricingServerAction(product.sku),
    enabled: isPricingEnabled(locale) && !pricing,
  });

  const msrp = pricing?.msrp ?? query.data?.msrp;
  const showAddToCart = isCartEnabled(locale) && isPricingEnabled(locale) && msrp;

  return (
    <Stack direction={direction} spacing={2} sx={{ width: '100%' }}>
      <ProductAccessoryImage data={product} direction={direction} />
      <Stack sx={{ flex: '1' }}>
        <Box sx={{ flex: '1' }}>
          <ProductTitle sku={product.sku} title={product.title} />
          <Typography variant="body2" marginBottom={1}>
            {product.sku}
          </Typography>
        </Box>
        {showAddToCart && (
          <>
            <Typography marginBottom={2} fontWeight={700}>
              {format.number(msrp, { style: 'currency', currency: 'USD' })}
            </Typography>

            <AddToCartButton
              sku={product.sku}
              productTitle={product.title}
              imageUrl={product.image}
              price={msrp}
            />
          </>
        )}
        {!showAddToCart && (
          <ProductLink sku={product.sku} title={product.title}>
            <Button fullWidth variant="contained" color="secondary">
              {t('Product.view-detail')}
            </Button>
          </ProductLink>
        )}
      </Stack>
    </Stack>
  );
};
